import { Injectable } from '@angular/core';
import { firstValueFrom, from, map, Observable, of, switchMap } from 'rxjs';
import { constants } from '../../constants';
import {
  EcommerceExternalPricing,
  SearchEngineProduct,
  ShoppingCart,
  ShoppingCartItem,
} from '../../models';
import { getUpdatedShoppingCartItems } from '../../utils/shopping-cart.utils';
import { LocalDataKey, LocalStorageService } from '../local-storage/local-storage.service';
import { MockService } from '../mock/mock.service';
import { SearchService } from '../search/search.service';
import { SessionsService } from '../sessions/sessions.service';
import { AddShoppingCartItemData, LocalShoppingCart, RemoveShoppingCartItemData, ShoppingCartDetail, ShoppingCartsService } from './shopping-carts.service';

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartsServiceMock implements ShoppingCartsService {
  private shoppingCartDetail: Observable<ShoppingCartDetail | null>;

  constructor(
    private localStorageService: LocalStorageService,
    private mockService: MockService,
    private searchService: SearchService,
    private sessionsService: SessionsService,
  ) {
    this.shoppingCartDetail = this.sessionsService.getSession().pipe(switchMap((session) => {
      if (session) {
        return this.getShoppingCartMock(session.userId).pipe(switchMap((shoppingCart) => this.getMockedChildren(shoppingCart)));
      }
      return this.getLocalShoppingCart().pipe(switchMap((localShoppingCart) => this.getMockedChildren(localShoppingCart)));
    }));
  }

  async addItem(data: AddShoppingCartItemData): Promise<void> {
    const session = await firstValueFrom(this.sessionsService.getSession());
    if (session) {
      const shoppingCart = await firstValueFrom(this.getShoppingCartMock(session.userId));
      if (!shoppingCart) {
        return this.mockService.createShoppingCart({
          currency: constants.defaultCurrency,
          items: [{
            ...data,
            addedAt: new Date(),
          }],
          id: session.userId,
          shippingAddressId: null,
          userId: session.userId,
        }).then();
      }
      return this.mockService.updateShoppingCart(session.userId, {
        items: getUpdatedShoppingCartItems({
          items: shoppingCart.items,
          action: 'add',
          addItemData: data,
        }),
      });
    }
    let localShoppingCart = await firstValueFrom(this.getLocalShoppingCart());
    if (!localShoppingCart) {
      localShoppingCart = {
        currency: constants.defaultCurrency,
        items: [{
          ...data,
          addedAt: new Date(),
        }],
        shippingAddressId: null,
      };
    } else {
      localShoppingCart = {
        ...localShoppingCart,
        items: getUpdatedShoppingCartItems({
          items: localShoppingCart.items,
          action: 'add',
          addItemData: data,
        }),
      };
    }
    this.localStorageService.set<LocalShoppingCart>(LocalDataKey.ShoppingCart, localShoppingCart);
  }

  get(): Observable<ShoppingCartDetail | null> {
    return this.shoppingCartDetail;
  }

  async removeItem(data: RemoveShoppingCartItemData): Promise<void> {
    const session = await firstValueFrom(this.sessionsService.getSession());
    if (session) {
      const shoppingCart = await firstValueFrom(this.getShoppingCartMock(session.userId));
      if (!shoppingCart) {
        return this.mockService.createShoppingCart({
          currency: constants.defaultCurrency,
          id: session.userId,
          items: [],
          shippingAddressId: null,
          userId: session.userId,
        }).then();
      }
      return this.mockService.updateShoppingCart(session.userId, {
        items: getUpdatedShoppingCartItems({
          action: 'remove',
          items: shoppingCart.items,
          removeItemData: data,
        }),
      });
    }
    let localShoppingCart = await firstValueFrom(this.getLocalShoppingCart());
    if (!localShoppingCart) {
      localShoppingCart = {
        currency: constants.defaultCurrency,
        items: [{
          ...data,
          addedAt: new Date(),
        }],
        shippingAddressId: null,
      };
    } else {
      localShoppingCart = {
        ...localShoppingCart,
        items: getUpdatedShoppingCartItems({
          action: 'remove',
          items: localShoppingCart.items,
          removeItemData: data,
        }),
      };
    }
    this.localStorageService.set<LocalShoppingCart>(LocalDataKey.ShoppingCart, localShoppingCart);
  }

  async synchronize(): Promise<void> {
    const session = await firstValueFrom(this.sessionsService.getSession());
    if (!session) {
      return;
    }
    const localShoppingCart = await firstValueFrom(this.getLocalShoppingCart());
    const shoppingCart = await firstValueFrom(this.getShoppingCartMock(session.userId));
    if (!shoppingCart) {
      return this.mockService.createShoppingCart({
        currency: constants.defaultCurrency,
        id: session.userId,
        items: localShoppingCart ? localShoppingCart.items : [],
        shippingAddressId: null,
        userId: session.userId,
      }).then();
    }
    if (localShoppingCart && !localShoppingCart.items.length) {
      return;
    }
    const shoppingCartProductItems = shoppingCart.items.filter((item) => item.resourceType === 'product');
    let newShoppingCartProductItems: ShoppingCartItem[] = [];
    (localShoppingCart?.items ?? []).forEach((item) => {
      const index = shoppingCartProductItems.findIndex((product) => product.resourceId === item.resourceId);
      if (index !== -1) {
        const shoppingCartProductItem = shoppingCartProductItems[index];
        newShoppingCartProductItems.push(shoppingCartProductItem.addedAt.getTime() > item.addedAt.getTime() ? shoppingCartProductItem : item);
        shoppingCartProductItems.splice(index, 1);
      } else {
        newShoppingCartProductItems.push(item);
      }
    });
    newShoppingCartProductItems = newShoppingCartProductItems.sort((a, b) => a.addedAt.getTime() - b.addedAt.getTime());
    if (newShoppingCartProductItems.length > constants.maxShoppingCartItems) {
      newShoppingCartProductItems = newShoppingCartProductItems.slice(-constants.maxShoppingCartItems);
    }
    const newShoppingCartItems = shoppingCart.items.filter((item) => item.resourceType !== 'product').concat(newShoppingCartProductItems);
    await this.mockService.updateShoppingCart(session.userId, { items: newShoppingCartItems });
  }

  async updateShippingAddress(shippingAddressId: string): Promise<void> {
    const session = await firstValueFrom(this.sessionsService.getSession());
    if (session) {
      await this.mockService.updateShoppingCart(session.userId, { shippingAddressId });
    }
    const localShoppingCart = await firstValueFrom(this.getLocalShoppingCart());
    this.localStorageService.set<LocalShoppingCart>(LocalDataKey.ShoppingCart, {
      ...localShoppingCart as LocalShoppingCart,
      shippingAddressId,
    });
  }

  private getLocalShoppingCart(): Observable<LocalShoppingCart | null> {
    return this.localStorageService.get<LocalShoppingCart>(LocalDataKey.ShoppingCart).pipe(map((localShoppingCart) => {
      if (localShoppingCart) {
        localShoppingCart.items = localShoppingCart.items.map((item) => {
          if (item.addedAt) {
            item.addedAt = new Date(item.addedAt);
          }
          return item;
        });
      }
      return localShoppingCart;
    }));
  }

  private getMockedChildren(shoppingCart: ShoppingCart | LocalShoppingCart | null): Observable<ShoppingCartDetail | null> {
    if (!shoppingCart) {
      return of(null);
    }
    return from(this.searchService.search<SearchEngineProduct>('products', {
      filters: [
        {
          field: 'id',
          operator: 'in',
          value: shoppingCart.items.map((item) => item.resourceId),
        },
      ],
      pagination: {
        documentsPerPage: constants.maxShoppingCartItems,
      },
    })).pipe(map((paginable) => {
      let subtotal = 0;
      let discounts = 0;
      let originalShippingCost = 0;
      let shippingCost = 0;
      const pricing: Record<string, EcommerceExternalPricing> = {};
      const detailItems = shoppingCart.items.map((item) => {
        if (item.resourceType === 'product') {
          item.addedAt = new Date(item.addedAt);
          const resource = paginable.data.find((product) => product.id === item.resourceId) ?? null;
          if (resource) {
            shippingCost += item.quantity * resource.pricing['debit'].shippingCost;
            originalShippingCost += item.quantity * resource.pricing['debit'].originalShippingCost;
            subtotal += item.quantity * resource.pricing['debit'].price;
            discounts += item.quantity * (resource.originalPrice - resource?.pricing['debit'].price);
            constants.paymentOptions.forEach((option) => {
              if (!pricing[option]) {
                pricing[option] = { ...resource.pricing[option] };
              } else {
                pricing[option].monthlyPayment += item.quantity * resource.pricing[option].monthlyPayment;
                pricing[option].originalShippingCost += item.quantity * resource.pricing[option].originalShippingCost;
                pricing[option].price += item.quantity * resource.pricing[option].price;
                pricing[option].shippingCost += item.quantity * resource.pricing[option].shippingCost;
              }
            });
            return {
              ...item,
              resource,
            };
          }
        }
        return {
          ...item,
          resource: null,
        };
      }).filter((item) => item.resource);
      let total = subtotal;
      if (shoppingCart.shippingAddressId) {
        total += shippingCost;
      }
      return {
        ...shoppingCart,
        discounts,
        items: detailItems,
        originalShippingCost,
        pricing,
        shippingCost,
        subtotal,
        total,
        totalItems: detailItems.reduce((totalItems, item) => totalItems + item.quantity, 0),
      };
    }));
  }

  private getShoppingCartMock(id: string): Observable<ShoppingCart | null> {
    return this.mockService.getShoppingCart(id);
  }
}
